import {ShieldCheckIcon} from "lucide-react";
import {useState} from "react";

import {VNextCheckResult, VNextChecksSummary} from "@hooks/use-vnext";

import {CHECK_GROUPS, TAB_CHECK_GROUPS} from "./check-labels";
import {ChecksPanel} from "./ChecksPanel";

interface TrustStripProps {
    checksSummary: VNextChecksSummary | null | undefined;
    checksScope?: "full_model";
    /** Which tab this strip is on — controls which check groups are shown. */
    tab?: "ceo_summary" | "pnl" | "cashflow" | "bs" | "bs_summary" | "summary";
}

const STALE_AFTER_MS = 24 * 60 * 60 * 1000;

function describeRunAge(runAt: string | null | undefined): {label: string; stale: boolean} | null {
    if (!runAt) return null;
    const ts = Date.parse(runAt);
    if (Number.isNaN(ts)) return null;
    const ageMs = Date.now() - ts;
    const stale = ageMs > STALE_AFTER_MS;
    const hours = Math.floor(ageMs / (60 * 60 * 1000));
    const label =
        hours < 1
            ? "checked just now"
            : hours < 24
              ? `checked ${hours}h ago`
              : `checked ${Math.floor(hours / 24)}d ago`;
    return {label, stale};
}

interface GroupStatus {
    label: string;
    status: "PASS" | "WARN" | "FAIL";
    /** Largest absolute delta across failed checks in this group. */
    maxDelta: number | null;
}

function computeGroupStatuses(
    results: VNextCheckResult[],
    tab: string,
): GroupStatus[] {
    const relevantGroupKeys = TAB_CHECK_GROUPS[tab] ?? TAB_CHECK_GROUPS.summary;
    const statuses: GroupStatus[] = [];

    for (const groupKey of relevantGroupKeys) {
        const group = CHECK_GROUPS[groupKey];
        if (!group) continue;

        const groupResults = results.filter((r) => group.checkIds.includes(r.check_id));
        if (groupResults.length === 0) continue;

        const hasFail = groupResults.some((r) => r.status === "FAIL");
        const hasWarn = groupResults.some((r) => r.status === "WARN");
        const status: "PASS" | "WARN" | "FAIL" = hasFail ? "FAIL" : hasWarn ? "WARN" : "PASS";

        let maxDelta: number | null = null;
        if (hasFail) {
            const failedResults = groupResults.filter((r) => r.status === "FAIL");
            maxDelta = failedResults.reduce(
                (max, r) => Math.max(max, Math.abs(r.delta)),
                0,
            );
        }

        statuses.push({label: group.label, status, maxDelta});
    }

    return statuses;
}

export function TrustStrip({checksSummary, checksScope, tab = "summary"}: TrustStripProps) {
    const [expanded, setExpanded] = useState(false);

    // Debug mode: show full ChecksPanel directly
    const isDebug = typeof window !== "undefined" && localStorage.getItem("pecunio_debug") === "true";
    if (isDebug) {
        return <ChecksPanel checksSummary={checksSummary} checksScope={checksScope} />;
    }

    if (!checksSummary || checksSummary.results.length === 0) return null;

    const groupStatuses = computeGroupStatuses(checksSummary.results, tab);
    if (groupStatuses.length === 0) return null;

    const overallFails = groupStatuses.filter((g) => g.status === "FAIL").length;
    const overallWarns = groupStatuses.filter((g) => g.status === "WARN").length;
    const overallStatus = overallFails > 0 ? "FAIL" : overallWarns > 0 ? "WARN" : "PASS";

    const headline =
        overallStatus === "PASS"
            ? "Actuals match Xero"
            : overallStatus === "WARN"
              ? `${overallWarns} warning${overallWarns !== 1 ? "s" : ""}`
              : `${overallFails} mismatch${overallFails !== 1 ? "es" : ""}`;

    const runAge = describeRunAge(checksSummary.run_at);

    return (
        <div>
            <div
                className={`rounded-md border text-xs cursor-pointer select-none transition-colors ${
                    overallStatus === "FAIL"
                        ? "border-red-200 bg-red-50/50"
                        : overallStatus === "WARN"
                          ? "border-amber-200 bg-amber-50/50"
                          : "border-green-200 bg-green-50/50"
                }`}
                onClick={() => setExpanded(!expanded)}
                data-testid="trust-strip"
            >
                <div className="flex items-center gap-2 px-3 py-1.5 flex-wrap">
                    <ShieldCheckIcon
                        className={`size-3.5 shrink-0 ${
                            overallStatus === "FAIL"
                                ? "text-red-600"
                                : overallStatus === "WARN"
                                  ? "text-amber-600"
                                  : "text-green-600"
                        }`}
                    />
                    <span className="font-medium">{headline}</span>
                    {runAge && (
                        <span
                            className={`text-[10px] ${
                                runAge.stale ? "text-amber-700 font-medium" : "text-muted-foreground"
                            }`}
                            data-testid="trust-strip-run-age"
                        >
                            {runAge.label}
                            {runAge.stale ? " — stale, re-run checks" : ""}
                        </span>
                    )}

                    <span className="text-muted-foreground mx-1">—</span>

                    <div className="flex items-center gap-2 flex-wrap">
                        {groupStatuses.map((g) => (
                            <span key={g.label} className="inline-flex items-center gap-0.5">
                                <span className="text-muted-foreground">{g.label}</span>
                                {g.status === "PASS" && (
                                    <span className="text-green-600 font-medium" aria-label={`${g.label} pass`}>&#10003;</span>
                                )}
                                {g.status === "WARN" && (
                                    <span className="text-amber-600 font-medium" aria-label={`${g.label} warning`}>&#9888;</span>
                                )}
                                {g.status === "FAIL" && (
                                    <span className="text-red-600 font-medium" aria-label={`${g.label} fail`}>
                                        &#10007;
                                        {g.maxDelta != null && g.maxDelta > 0 && (
                                            <span className="ml-0.5 text-[10px]">
                                                (${g.maxDelta.toLocaleString()})
                                            </span>
                                        )}
                                    </span>
                                )}
                                {g !== groupStatuses[groupStatuses.length - 1] && (
                                    <span className="text-muted-foreground ml-1">|</span>
                                )}
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            {expanded && (
                <div className="mt-1">
                    <ChecksPanel checksSummary={checksSummary} checksScope={checksScope} />
                </div>
            )}
        </div>
    );
}
